import { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  Building2,
  MapPin,
  Wrench,
  Bell,
  Plug,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';

interface FleetSettings {
  id: string;
  tenant_id: string;
  type: string;
  company_name: string;
  timezone: string;
  distance_unit: 'miles' | 'kilometers';
  gps_provider: string;
  gps_api_key: string;
  gps_polling_interval: number;
  maintenance_oil_change_interval: number;
  maintenance_tire_rotation_interval: number;
  maintenance_alert_days: number;
  maintenance_alert_miles: number;
  notifications_email: boolean;
  notifications_sms: boolean;
  integrations: {
    psa: { connected: boolean; last_tested?: string };
    books: { connected: boolean; last_tested?: string };
    bus: { connected: boolean; last_tested?: string };
  };
  created_at: string;
  updated_at: string;
}

const inputCls = 'w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';
const labelCls = 'block text-sm font-medium text-zinc-400 mb-1.5';
const selectCls = 'w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 appearance-none';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Berlin',
  'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

type SettingsForm = Omit<FleetSettings, 'id' | 'tenant_id' | 'type' | 'created_at' | 'updated_at' | 'integrations'>;

export default function AdminPage() {
  const { data: settings, isLoading, error: loadError, refetch } = useApi<FleetSettings>('/api/settings');

  const [form, setForm] = useState<SettingsForm | null>(null);
  const [original, setOriginal] = useState<SettingsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [togglingNotif, setTogglingNotif] = useState<string | null>(null);
  const [testingIntegration, setTestingIntegration] = useState<string | null>(null);
  const [integrationResults, setIntegrationResults] = useState<Record<string, { connected: boolean; message: string }>>({});
  const [integrations, setIntegrations] = useState(settings?.integrations);

  // Sync settings to form state
  useEffect(() => {
    if (settings) {
      const formData: SettingsForm = {
        company_name: settings.company_name,
        timezone: settings.timezone,
        distance_unit: settings.distance_unit,
        gps_provider: settings.gps_provider,
        gps_api_key: settings.gps_api_key,
        gps_polling_interval: settings.gps_polling_interval,
        maintenance_oil_change_interval: settings.maintenance_oil_change_interval,
        maintenance_tire_rotation_interval: settings.maintenance_tire_rotation_interval,
        maintenance_alert_days: settings.maintenance_alert_days,
        maintenance_alert_miles: settings.maintenance_alert_miles,
        notifications_email: settings.notifications_email,
        notifications_sms: settings.notifications_sms,
      };
      setForm(formData);
      setOriginal(formData);
      setIntegrations(settings.integrations);
    }
  }, [settings]);

  const isDirty = form && original && JSON.stringify(form) !== JSON.stringify(original);

  const updateField = useCallback(<K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
    setForm(f => f ? { ...f, [key]: value } : f);
    setSaveMsg(null);
  }, []);

  // Save all form changes
  const handleSave = async () => {
    if (!form || !isDirty) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const changes: Record<string, unknown> = {};
      const keys: (keyof SettingsForm)[] = [
        'company_name', 'timezone', 'distance_unit', 'gps_provider', 'gps_api_key',
        'gps_polling_interval', 'maintenance_oil_change_interval', 'maintenance_tire_rotation_interval',
        'maintenance_alert_days', 'maintenance_alert_miles', 'notifications_email', 'notifications_sms',
      ];
      for (const key of keys) {
        if (form[key] !== original![key]) {
          changes[key] = form[key];
        }
      }
      await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify(changes),
      });
      setSaveMsg({ type: 'success', text: 'Settings saved successfully' });
      setOriginal({ ...form });
    } catch (err) {
      setSaveMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  // Toggle notification immediately
  const toggleNotification = async (field: 'notifications_email' | 'notifications_sms') => {
    if (!form) return;
    const newValue = !form[field];
    setTogglingNotif(field);
    try {
      await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({ [field]: newValue }),
      });
      setForm(f => f ? { ...f, [field]: newValue } : f);
      setOriginal(o => o ? { ...o, [field]: newValue } : o);
    } catch {
      // revert on failure
    } finally {
      setTogglingNotif(null);
    }
  };

  // Test integration connection
  const testIntegration = async (product: string) => {
    setTestingIntegration(product);
    try {
      const result = await apiFetch<{ product: string; connected: boolean; message: string }>(
        `/api/settings/integrations/${product}/test`,
        { method: 'POST' },
      );
      setIntegrationResults(r => ({ ...r, [product]: { connected: result.connected, message: result.message } }));
      setIntegrations((prev: FleetSettings['integrations'] | undefined) => prev ? {
        ...prev,
        [product]: { connected: result.connected, last_tested: new Date().toISOString() },
      } : prev);
    } catch (err) {
      setIntegrationResults(r => ({ ...r, [product]: { connected: false, message: err instanceof Error ? err.message : 'Test failed' } }));
    } finally {
      setTestingIntegration(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="rounded-xl border border-rose-800 bg-rose-900/20 p-6 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-rose-400 mb-3" />
        <p className="text-sm text-rose-300">{loadError.message}</p>
        <button onClick={refetch} className="mt-3 text-sm text-emerald-400 hover:text-emerald-300">
          Try again
        </button>
      </div>
    );
  }

  if (!form) return null;

  const integrationList = [
    { key: 'psa', name: 'The One PSA', description: 'Sync vehicles with client assets and service tickets' },
    { key: 'books', name: 'The One Books', description: 'Push fuel and maintenance costs to accounting' },
    { key: 'bus', name: 'The One Bus', description: 'Publish fleet events to the activity bus' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-zinc-100">Fleet Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          {saveMsg && (
            <span className={`text-sm ${saveMsg.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
              {saveMsg.text}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* General Settings */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Building2 className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-zinc-100">General</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label className={labelCls}>Company Name</label>
            <input
              type="text"
              className={inputCls}
              value={form.company_name}
              onChange={e => updateField('company_name', e.target.value)}
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className={labelCls}>Timezone</label>
            <select
              className={selectCls}
              value={form.timezone}
              onChange={e => updateField('timezone', e.target.value)}
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Distance Unit</label>
            <select
              className={selectCls}
              value={form.distance_unit}
              onChange={e => updateField('distance_unit', e.target.value as 'miles' | 'kilometers')}
            >
              <option value="miles">Miles</option>
              <option value="kilometers">Kilometers</option>
            </select>
          </div>
        </div>
      </div>

      {/* GPS Provider */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-3 mb-5">
          <MapPin className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-zinc-100">GPS Provider</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label className={labelCls}>Provider</label>
            <input
              type="text"
              className={inputCls}
              value={form.gps_provider}
              onChange={e => updateField('gps_provider', e.target.value)}
              placeholder="e.g. Samsara, Geotab"
            />
          </div>
          <div>
            <label className={labelCls}>API Key</label>
            <input
              type="text"
              className={inputCls + ' font-mono'}
              value={form.gps_api_key}
              onChange={e => updateField('gps_api_key', e.target.value)}
              placeholder="Enter API key"
            />
          </div>
          <div>
            <label className={labelCls}>Polling Interval (seconds)</label>
            <input
              type="number"
              className={inputCls}
              value={form.gps_polling_interval}
              onChange={e => updateField('gps_polling_interval', parseInt(e.target.value, 10) || 30)}
              min={5}
              max={3600}
            />
          </div>
        </div>
      </div>

      {/* Maintenance */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Wrench className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-zinc-100">Maintenance</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label className={labelCls}>Default Oil Change Interval ({form.distance_unit})</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_oil_change_interval}
              onChange={e => updateField('maintenance_oil_change_interval', parseInt(e.target.value, 10) || 5000)}
              min={100}
              max={100000}
            />
          </div>
          <div>
            <label className={labelCls}>Default Tire Rotation Interval ({form.distance_unit})</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_tire_rotation_interval}
              onChange={e => updateField('maintenance_tire_rotation_interval', parseInt(e.target.value, 10) || 7500)}
              min={100}
              max={100000}
            />
          </div>
          <div>
            <label className={labelCls}>Alert Threshold (days before due)</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_alert_days}
              onChange={e => updateField('maintenance_alert_days', parseInt(e.target.value, 10) || 14)}
              min={1}
              max={365}
            />
          </div>
          <div>
            <label className={labelCls}>Alert Threshold ({form.distance_unit} before due)</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_alert_miles}
              onChange={e => updateField('maintenance_alert_miles', parseInt(e.target.value, 10) || 500)}
              min={50}
              max={10000}
            />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Bell className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-zinc-100">Notifications</h2>
        </div>
        <div className="space-y-4">
          {([
            { field: 'notifications_email' as const, label: 'Email Alerts', desc: 'Receive maintenance reminders and critical alerts via email' },
            { field: 'notifications_sms' as const, label: 'SMS Alerts', desc: 'Receive urgent notifications via text message' },
          ]).map(({ field, label, desc }) => {
            const on = form[field];
            const toggling = togglingNotif === field;
            return (
              <div key={field} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-800/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-zinc-200">{label}</p>
                  <p className="text-xs text-zinc-500">{desc}</p>
                </div>
                <button
                  onClick={() => toggleNotification(field)}
                  disabled={toggling}
                  className="relative"
                >
                  {toggling ? (
                    <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                  ) : (
                    <div className={`h-6 w-11 rounded-full p-0.5 transition-colors ${on ? 'bg-emerald-600' : 'bg-zinc-700'}`}>
                      <div className={`h-5 w-5 rounded-full transition-transform ${on ? 'bg-white translate-x-5' : 'bg-zinc-400'}`} />
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Integrations */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Plug className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-zinc-100">Integrations</h2>
        </div>
        <div className="space-y-3">
          {integrationList.map(({ key, name, description }) => {
            const status = integrations?.[key as keyof typeof integrations];
            const testing = testingIntegration === key;
            const result = integrationResults[key];
            return (
              <div
                key={key}
                className="rounded-lg border border-zinc-800 bg-zinc-800/50 px-4 py-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-200">{name}</p>
                    <p className="text-xs text-zinc-500">{description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {status?.connected ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          <span className="text-xs font-medium text-emerald-400">Connected</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-zinc-500" />
                          <span className="text-xs font-medium text-zinc-500">Not Connected</span>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => testIntegration(key)}
                      disabled={testing}
                      className="flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition hover:bg-zinc-700 disabled:opacity-40"
                    >
                      {testing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      Test
                    </button>
                  </div>
                </div>
                {result && (
                  <div className={`mt-2 rounded px-3 py-1.5 text-xs ${result.connected ? 'bg-emerald-900/30 text-emerald-300' : 'bg-rose-900/30 text-rose-300'}`}>
                    {result.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
